import React from 'react';
import { Box, Button, IconButton, Stack, Switch, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { emptyImage } from './emptyProduct';

/**
 * Product images list with add / edit / primary / delete per row.
 *
 * Exactly one image should be marked primary (it's used on the shop
 * grid and as the OG image). Toggling primary on a row clears it on
 * the others. Deleting the current primary promotes the first
 * remaining image so we never end up with zero primaries.
 */
export default function ProductImagesEditor({ form, setForm }) {
  const images = form.images || [];

  const setImages = (next) => setForm((f) => ({ ...f, images: next }));
  const updateOne = (i, patch) =>
    setImages(images.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const addImage = () => setImages([...images, emptyImage(images)]);

  const setPrimary = (i, checked) => {
    setImages(images.map((x, j) => ({
      ...x,
      is_primary: checked ? j === i : (j === i ? false : x.is_primary),
    })));
  };

  const removeImage = (i) => {
    const kept = images.filter((_, j) => j !== i);
    const hasPrimary = kept.some((x) => x.is_primary);
    setImages(kept.map((x, j) => (j === 0 && !hasPrimary ? { ...x, is_primary: true } : x)));
  };

  return (
    <Box>
      <Stack direction='row' alignItems='center' justifyContent='space-between' mb='6px'>
        <Typography fontWeight='700' variant='body2'>Images</Typography>
        <Button size='small' startIcon={<AddIcon />} onClick={addImage}>Add image</Button>
      </Stack>
      <Typography variant='caption' color='text.secondary' mb='8px' display='block'>
        Mark one image as primary — it's the one used on listings. Others appear in the storefront gallery.
      </Typography>
      {images.length === 0 && (
        <Typography variant='caption' color='text.secondary' fontStyle='italic' mb='6px' display='block'>
          No images yet.
        </Typography>
      )}
      {images.map((img, i) => (
        <Stack
          key={`img-${i}`}
          direction={{ xs: 'column', md: 'row' }}
          spacing='8px' mb='6px'
          alignItems={{ md: 'center' }}
        >
          <Box
            sx={{
              width: { xs: '100%', md: '52px' },
              height: '52px', borderRadius: '6px', overflow: 'hidden',
              bgcolor: 'action.hover', flexShrink: 0,
            }}
          >
            {img.url && (
              <img src={img.url} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            )}
          </Box>
          <TextField
            size='small' value={img.url} placeholder='https://…'
            onChange={(e) => updateOne(i, { url: e.target.value })}
            sx={{ flex: 2 }}
          />
          <TextField
            size='small' value={img.alt || ''} placeholder='Alt text'
            onChange={(e) => updateOne(i, { alt: e.target.value })}
            sx={{ flex: 1 }}
          />
          <Stack direction='row' alignItems='center' spacing='6px'>
            <Switch
              size='small' checked={!!img.is_primary}
              onChange={(e) => setPrimary(i, e.target.checked)}
            />
            <Typography variant='caption' color='text.secondary'>Primary</Typography>
            <IconButton size='small' color='error' onClick={() => removeImage(i)}>
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Stack>
        </Stack>
      ))}
    </Box>
  );
}
